import { useMemo, useState } from 'react';
import { PlusIcon, SearchIcon, UsersIcon } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import {
  createCustomer,
  customersQueryKey,
  listCustomers,
  updateCustomer,
  type CreateCustomerRequest,
  type UpdateCustomerRequest,
} from '@/lib/api/customers';

import { AddCustomerModal } from './add-customer-modal';
import { CustomerDetailModal } from './customer-detail-modal';
import { CustomersGrid } from './customers-grid';
import type { StatusFilter } from './types';

export function Customers() {
  const { t } = useLanguage();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [isAdding, setIsAdding] = useState(false);
  const [activeCustomerId, setActiveCustomerId] = useState<string | null>(null);

  // ── Data ──────────────────────────────────────────────────────────────────────

  const customersQuery = useQuery({
    queryKey: customersQueryKey(businessId ?? '', statusFilter),
    queryFn: () =>
      listCustomers(businessId!, statusFilter === 'all' ? undefined : statusFilter),
    enabled: Boolean(businessId),
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateCustomerRequest) => createCustomer(businessId!, body),
    meta: { successMessage: t.customerAdded },
    onSuccess: () => setIsAdding(false),
  });

  const updateMutation = useMutation({
    mutationFn: (body: UpdateCustomerRequest) =>
      updateCustomer(businessId!, activeCustomerId!, body),
    meta: { successMessage: t.customerUpdated },
    onSuccess: () => setActiveCustomerId(null),
  });

  const customers = customersQuery.data ?? [];
  const activeCount = customers.filter((c) => c.status === 'active').length;

  // ── Derived ───────────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = customersQuery.data ?? [];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.full_name.toLowerCase().includes(q) ||
          (c.email ?? '').toLowerCase().includes(q) ||
          (c.phone ?? '').includes(q),
      );
    }

    return list;
  }, [customersQuery.data, search]);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <UsersIcon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-tight">{t.customersNav}</h1>
            <p className="text-xs text-muted-foreground">
              {activeCount} {t.customerActive.toLowerCase()} · {customers.length}{' '}
              {t.customersNav.toLowerCase()}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          className="gap-1.5"
          data-testid="add-customer"
          onClick={() => setIsAdding(true)}
        >
          <PlusIcon className="w-4 h-4" />
          {t.addCustomer}
        </Button>
      </div>

      {/* Search + status tabs */}
      <div className="flex flex-col gap-3 mb-5">
        <div className="relative max-w-sm">
          <SearchIcon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.searchCustomers}
            className="pl-8 text-sm"
            data-testid="customer-search"
          />
        </div>

        <div className="flex gap-1 border-b">
          {(['all', 'active', 'inactive'] as const).map((s) => {
            const label =
              s === 'all'
                ? t.allStatuses
                : s === 'active'
                  ? t.customerActive
                  : t.customerInactive;

            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={[
                  'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                  statusFilter === s
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                ].join(' ')}
                data-testid={`filter-status-${s}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {customersQuery.isPending ? (
        <p className="text-sm text-muted-foreground py-10 text-center">{t.loading}</p>
      ) : (
        <CustomersGrid customers={filtered} onSelect={setActiveCustomerId} />
      )}

      {isAdding && (
        <AddCustomerModal
          onClose={() => setIsAdding(false)}
          onSave={(body) => createMutation.mutate(body)}
          isSaving={createMutation.isPending}
        />
      )}

      {activeCustomerId && businessId && (
        <CustomerDetailModal
          businessId={businessId}
          customerId={activeCustomerId}
          onClose={() => setActiveCustomerId(null)}
          onSave={(body) => updateMutation.mutate(body)}
          isSaving={updateMutation.isPending}
        />
      )}
    </div>
  );
}
